import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getArticleById, articles } from '@/mocks/articles';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-10 bg-background-50 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-4xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-500 text-sm">文章未找到</p>
            <Link to="/articles" className="mt-4 inline-block text-sm text-primary-500 hover:text-primary-600">
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '麻豆精品影视网',
    },
    url: `https://www.madoujipin.com/article/${article.id}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-10 md:pb-14 bg-background-50">
        <div className="px-4 md:px-6 max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '影视资讯', path: '/articles' },
              { label: article.title },
            ]}
          />

          <article className="mt-6 md:mt-8">
            <div className="mb-6 md:mb-8">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="px-2 py-0.5 bg-accent-100 text-accent-800 text-xs font-medium rounded">
                  {article.category}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 mt-4 text-sm text-foreground-400 flex-wrap">
                <span>
                  <i className="ri-user-line mr-1"></i>
                  {article.author}
                </span>
                <span>
                  <i className="ri-calendar-line mr-1"></i>
                  {article.publishDate}
                </span>
                <span>
                  <i className="ri-eye-line mr-1"></i>
                  {article.readCount}阅读
                </span>
              </div>
            </div>

            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-6 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
                <span className="font-medium">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="mt-12 md:mt-16">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-5">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-100 rounded-lg overflow-hidden hover:bg-background-200/70 transition-colors cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3 md:p-4">
                      <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-1">{ra.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}